<form class="medications-form" *ngIf="medicationsArray && medicationsArray.controls" (ngSubmit)="onSave()">
  <div class="modal-header">
    <h2>{{ title }}</h2>
    <span class="staged-counter" *ngIf="medicationCount > 1">{{ medicationCount }} added</span>
  </div>

  <div *ngFor="let group of medicationsArray.controls; let i = index" class="medication-card" [formGroup]="group">
    <div class="card-summary" *ngIf="!isExpanded(i)">
      <button mat-icon-button type="button" class="expand-btn" (click)="expandCard(i)" aria-label="Expand medication">
        <mat-icon>expand_more</mat-icon>
      </button>
      <div class="summary-text" (click)="expandCard(i)">
        <span class="summary-name">{{ getSummaryName(i) }}</span>
        <span class="summary-meta">{{ getSummary(i) }}</span>
      </div>
      <button
        mat-icon-button
        type="button"
        class="remove-btn"
        *ngIf="medicationCount > 1"
        (click)="removeStagedCard(i)"
        aria-label="Remove medication"
      >
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <div *ngIf="isExpanded(i)" class="card-body">
      <div class="card-body-header" *ngIf="medicationCount > 1">
        <span class="card-label">Medication {{ i + 1 }}</span>
        <button
          mat-icon-button
          type="button"
          class="remove-btn"
          (click)="removeStagedCard(i)"
          aria-label="Remove medication"
          matTooltip="Remove this medication"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div class="medication__basic-info">
        <div fxLayout="row">
          <mat-form-field fxFlex>
            <mat-label>Drug Name</mat-label>
            <input
              type="text"
              matInput
              formControlName="name"
              [matAutocomplete]="auto"
              placeholder="Enter at least 3 characters"
            />
            <mat-autocomplete #auto="matAutocomplete">
              <mat-option *ngFor="let option of filteredOptions[i] | async" [value]="option">
                {{ option }}
              </mat-option>
            </mat-autocomplete>
            <mat-hint *ngIf="isDuplicateName(i)" class="duplicate-warning">Already on patient's chart.</mat-hint>
          </mat-form-field>
        </div>

        <div fxLayout="row">
          <mat-form-field class="amount-form-field">
            <mat-label>Amount</mat-label>
            <input type="text" matInput formControlName="dosageAmount" />
          </mat-form-field>

          <mat-form-field class="units-form-field">
            <mat-label>Units</mat-label>
            <mat-select formControlName="dosageMeasure">
              <mat-option *ngFor="let dose of doses" [value]="dose">
                {{ dose }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <!--
          BRD-CLIN-2026-006 §4.2's ceiling, computed LIVE from this group as the MA types rather than
          on save — the useful moment is while the dose is still being decided, not after it is
          committed. Renders only when the list resolved a `ceilingContext`, i.e. the clinic carries
          the CHF care-call toggle AND the patient's diagnoses resolve to CHF.

          The two states are styled apart on purpose. "At or above the ceiling" is a finding the MA
          must act on; "cannot be calculated yet" is an incomplete record, and dressing that as an
          alarm would teach them to dismiss the one that matters. It also appears constantly while
          they type, since a half-entered dose is by definition incomplete.
        -->
        <div
          *ngIf="ceilingNoteFor(group) as note"
          class="rx-ceiling-note"
          [class.rx-ceiling-note--warn]="note.warn"
          role="status"
        >
          <mat-icon class="rx-ceiling-note__icon">{{ note.warn ? 'report_problem' : 'help_outline' }}</mat-icon>
          <span>{{ note.text }}</span>
        </div>
        <div>
          <mat-form-field class="drug-form-field">
            <mat-label>Drug Form</mat-label>
            <mat-select formControlName="drugForm">
              <mat-option *ngFor="let type of doseTypes" [value]="type">
                {{ type }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>
        <div class="frequency-container">
          <mat-form-field class="frequency-form-field">
            <mat-label>Frequency</mat-label>
            <mat-select formControlName="frequency">
              <mat-option *ngFor="let option of frequencyOptions" [value]="option.value" matTooltip="{{ option.name }}">
                {{ option.value }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <div *ngIf="group.get('frequency')?.value === 'Other'">
            <mat-form-field class="input-form-field">
              <mat-label>Other</mat-label>
              <input matInput formControlName="otherFrequency" placeholder="Enter frequency" required />
              <mat-error *ngIf="group.errors?.otherFrequencyRequired">Required when frequency is Other.</mat-error>
            </mat-form-field>
          </div>
        </div>

        <div formGroupName="selectedHours">
          <label>Hours</label>
          <div class="checkbox-group">
            <mat-checkbox *ngFor="let hour of hours" [formControlName]="hour">
              {{ hour }}
            </mat-checkbox>
          </div>
        </div>
      </div>

      <div class="medication__state">
        <div fxLayout="row">
          <mat-form-field fxFlex>
            <mat-label>Status</mat-label>
            <mat-select formControlName="status">
              <mat-option *ngFor="let option of statusOptions" [value]="option.value">
                {{ option.label }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <div fxLayout="row" *ngIf="shouldShowStatusReason(i)">
          <mat-form-field fxFlex>
            <mat-label>Status Reason</mat-label>
            <mat-select formControlName="statusReason">
              <mat-option *ngFor="let reason of statusReasonOptions" [value]="reason">
                {{ reason }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <div fxLayout="row">
          <mat-form-field fxFlex>
            <mat-label>Prior Prescription (if replacing)</mat-label>
            <mat-select formControlName="priorPrescription">
              <mat-option value="">None</mat-option>
              <mat-option *ngFor="let med of availablePriorMedications" [value]="med.id">
                {{ med.name }} - {{ med.dosage }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>
      </div>

      <div class="reminder-container">
        <div fxLayout="column" fxFlex>
          <label>Reminder</label>
          <mat-slide-toggle formControlName="reminder">On</mat-slide-toggle>
        </div>
        <div *ngIf="group.get('reminder')?.value" class="reminder-content">
          <mat-card class="schedule-card">
            <div>
              <mat-card-subtitle>When will the medication be taken?</mat-card-subtitle>
              <mat-form-field class="frequency-form-field">
                <mat-select formControlName="reminderFrequency">
                  <mat-option *ngFor="let option of reminderFrequencyOptions" [value]="option.value">
                    {{ option.name }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </div>

            <mat-divider></mat-divider>

            <div>
              <mat-card-subtitle>{{
                group.get('reminderFrequency')?.value === 'specific_days' ? 'On this days:' : 'At what time?'
              }}</mat-card-subtitle>

              <div *ngIf="group.get('reminderFrequency')?.value === 'specific_days'" class="day-row">
                <button
                  mat-raised-button
                  type="button"
                  class="day-btn"
                  *ngFor="let d of days"
                  [ngClass]="selectedDayClass(i, d.key)"
                  (click)="setSelectedDays(i, d.key)"
                >
                  {{ d.label }}
                </button>
              </div>

              <div class="times">
                <div class="time-row" *ngFor="let time of reminderTimesAt(i).controls; let r = index">
                  <button mat-icon-button type="button" color="warn" (click)="removeTime(i, r)">
                    <mat-icon>remove_circle</mat-icon>
                  </button>

                  <mat-form-field appearance="fill" class="time-select">
                    <mat-label>Time</mat-label>
                    <mat-select [formControl]="time">
                      <mat-option *ngFor="let t of hourOptions" [value]="t" [disabled]="isDisabledOption(t, i, r)">{{
                        t
                      }}</mat-option>
                    </mat-select>
                  </mat-form-field>
                </div>

                <mat-divider></mat-divider>

                <button mat-button type="button" class="add-time-btn" color="accent" (click)="addTime(i)">
                  <div class="add-time-btn-content">
                    <mat-icon>add_circle</mat-icon>
                    Add a Time
                  </div>
                </button>
              </div>
            </div>
          </mat-card>
        </div>
      </div>
    </div>
  </div>

  <div class="add-another-row" *ngIf="canAddAnother">
    <button
      mat-raised-button
      type="button"
      color="primary"
      (click)="addAnotherMedication()"
      class="add-another-btn"
      [disabled]="atMedicationLimit"
    >
      <mat-icon>add</mat-icon>
      Add another medication
    </button>
    <span class="limit-hint" *ngIf="atMedicationLimit">Maximum of {{ maxBulkMedications }} medications per save.</span>
  </div>

  <div class="medication-form-buttons">
    <button mat-button type="button" (click)="onCancel()">Cancel</button>
    <button mat-raised-button color="primary" [disabled]="medicationsArray?.invalid || loading" type="submit">
      {{ saveLabel }}
    </button>
  </div>
  <mat-progress-bar *ngIf="loading" mode="indeterminate"></mat-progress-bar>
</form>
